import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, Send, Inbox, Settings, Mail, PenSquare, PenLine, Trash2, Folder, FolderPlus, FileText, Reply, Forward, Paperclip, Download, MailOpen, ListTodo, UserPlus, Search, X, Bold, Italic, Underline, Link2, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, CalendarClock, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VoiceButton from "@/components/VoiceButton";
import { Sparkles } from "lucide-react";

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

const PRESETS = {
  microsoft: { label: "Microsoft 365 / Outlook", host: "outlook.office365.com", port: 993, smtp_host: "smtp.office365.com", smtp_port: 587 },
  gmail: { label: "Gmail / Google Workspace", host: "imap.gmail.com", port: 993, smtp_host: "smtp.gmail.com", smtp_port: 587 },
};

const folderMeta = (name) => {
  const n = name.toLowerCase();
  if (n === "inbox") return { label: "Inbox", icon: Inbox };
  if (n.includes("sent")) return { label: "Sent", icon: Send };
  if (n.includes("draft")) return { label: "Drafts", icon: FileText };
  if (n.includes("trash") || n.includes("deleted") || n.includes("bin")) return { label: "Bin", icon: Trash2 };
  if (n.includes("junk") || n.includes("spam")) return { label: "Spam", icon: Mail };
  return { label: name, icon: Folder };
};
const folderRank = (name) => {
  const n = name.toLowerCase();
  if (n === "inbox") return 0;
  if (n.includes("sent")) return 1;
  if (n.includes("draft")) return 2;
  if (n.includes("trash") || n.includes("deleted") || n.includes("bin")) return 3;
  if (n.includes("junk") || n.includes("spam")) return 4;
  return 5;
};

const senderName = (from) => {
  const m = (from || "").match(/^\s*"?([^"<]+)"?\s*</);
  return (m ? m[1] : from || "").trim() || "(unknown)";
};
const senderEmail = (from) => ((from || "").match(/<([^>]+)>/) || [null, from || ""])[1];
const shortDate = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return d || "";
  const today = new Date();
  return dt.toDateString() === today.toDateString()
    ? dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const escHtml = (t) => (t || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const textToHtml = (t) => escHtml(t).replace(/\n/g, "<br>");
const stripHtml = (h) => { const d = document.createElement("div"); d.innerHTML = h || ""; return d.innerText || ""; };

const RichEditor = ({ value, onChange, editorRef, testid }) => {
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== (value || "")) el.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const exec = (cmd, arg = null) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(cmd, false, arg);
    onChange(el.innerHTML);
  };
  const insertLink = () => {
    const url = window.prompt("Web address to link to (https://…)", "https://");
    if (url && url !== "https://") exec("createLink", url);
  };
  const TBtn = ({ title, cmd, arg, tid, onClick, children }) => (
    <button type="button" title={title} aria-label={title} data-testid={tid}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick || (() => exec(cmd, arg))}
      className="h-8 min-w-8 px-1.5 rounded-md text-slate-600 hover:bg-slate-200 hover:text-[#005EB8] transition-colors grid place-items-center">
      {children}
    </button>
  );
  return (
    <div className="rounded-lg border border-slate-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#005EB8]">
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 bg-slate-50 border-b border-slate-200" data-testid={`${testid}-toolbar`}>
        <TBtn title="Undo" cmd="undo" tid={`${testid}-undo`}><Undo2 className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Redo" cmd="redo" tid={`${testid}-redo`}><Redo2 className="h-3.5 w-3.5" /></TBtn>
        <span className="w-px h-5 bg-slate-300 mx-0.5" />
        <TBtn title="Bold" cmd="bold" tid={`${testid}-bold`}><Bold className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Italic" cmd="italic" tid={`${testid}-italic`}><Italic className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Underline" cmd="underline" tid={`${testid}-underline`}><Underline className="h-3.5 w-3.5" /></TBtn>
        <span className="w-px h-5 bg-slate-300 mx-0.5" />
        <TBtn title="Insert link" tid={`${testid}-link`} onClick={insertLink}><Link2 className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Bullet list" cmd="insertUnorderedList" tid={`${testid}-bullets`}><List className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Numbered list" cmd="insertOrderedList" tid={`${testid}-numbered`}><ListOrdered className="h-3.5 w-3.5" /></TBtn>
        <span className="w-px h-5 bg-slate-300 mx-0.5" />
        <TBtn title="Align left" cmd="justifyLeft" tid={`${testid}-align-left`}><AlignLeft className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Align centre" cmd="justifyCenter" tid={`${testid}-align-center`}><AlignCenter className="h-3.5 w-3.5" /></TBtn>
        <TBtn title="Align right" cmd="justifyRight" tid={`${testid}-align-right`}><AlignRight className="h-3.5 w-3.5" /></TBtn>
        <span className="w-px h-5 bg-slate-300 mx-0.5" />
        <TBtn title="Smaller text" cmd="fontSize" arg="2" tid={`${testid}-smaller`}><span className="text-[10px] font-black">A−</span></TBtn>
        <TBtn title="Larger text" cmd="fontSize" arg="5" tid={`${testid}-larger`}><span className="text-sm font-black">A+</span></TBtn>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning data-testid={testid}
        onInput={() => onChange(editorRef.current.innerHTML)}
        className="min-h-[220px] max-h-[45vh] overflow-y-auto px-3 py-2.5 text-sm focus:outline-none [&_a]:text-[#005EB8] [&_a]:underline" />
    </div>
  );
};

const ContactPicker = ({ clients, onPick, testid }) => {
  const [cid, setCid] = useState("");
  const sel = clients.find((c) => c.id === cid);
  const people = sel ? [...(sel.contacts || []).filter((p) => p.email)] : [];
  if (sel?.email && !people.some((p) => p.email === sel.email)) people.unshift({ name: sel.name, email: sel.email, role: "Main" });
  return (
    <div className="flex gap-1.5 shrink-0">
      <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid={`${testid}-company`}
        className="h-11 w-32 rounded-lg border border-slate-300 px-2 text-xs bg-white">
        <option value="">Company…</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <select value="" onChange={(e) => { if (e.target.value) onPick(e.target.value); }} disabled={!cid} data-testid={`${testid}-person`}
        className="h-11 w-36 rounded-lg border border-slate-300 px-2 text-xs bg-white disabled:opacity-40">
        <option value="">{sel ? "Person…" : "Pick company"}</option>
        {people.map((p, i) => (
          <option key={p.contact_id || i} value={p.email}>{p.name}{p.role ? ` (${p.role})` : ""}</option>
        ))}
      </select>
    </div>
  );
};

const EmailTemplateEditor = () => {
  const [tpls, setTpls] = useState(null);
  const [savingTpl, setSavingTpl] = useState("");
  useEffect(() => {
    api.get("/admin/email-templates").then(({ data }) => setTpls(data)).catch(() => {});
  }, []);
  if (!tpls) return null;
  const save = async (key) => {
    setSavingTpl(key);
    try {
      const { data } = await api.put(`/admin/email-templates/${key}`, { subject: tpls[key].subject, body: tpls[key].body });
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err)); }
    setSavingTpl("");
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="email-templates">
      <div>
        <h2 className="font-heading text-base font-bold text-[#0F2547]">Email wording — every automatic email</h2>
        <p className="text-xs text-slate-500">Change the subject and message of any email the system sends to clients or staff. Words in {"{curly braces}"} are filled in automatically (names, links, amounts) — {"{details}"} marks where the login table, shift details or button goes. Leave a blank line between paragraphs.</p>
      </div>
      {["Clients", "Staff"].map((aud) => {
        const entries = Object.entries(tpls).filter(([, t]) => (t.audience || "Clients") === aud);
        if (!entries.length) return null;
        return (
          <div key={aud} className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-1" data-testid={`tpl-group-${aud.toLowerCase()}`}>Emails to {aud.toLowerCase()}</p>
            {entries.map(([key, t]) => (
              <div key={key} className="space-y-2 rounded-xl border border-slate-100 p-3" data-testid={`tpl-${key}`}>
                <p className="text-xs font-bold text-[#005EB8] uppercase tracking-wide">{t.label}</p>
                <p className="text-[11px] text-slate-400">{t.hint}</p>
                <input value={t.subject} onChange={(e) => setTpls((x) => ({ ...x, [key]: { ...x[key], subject: e.target.value } }))}
                  placeholder="Subject line" data-testid={`tpl-subject-${key}`} className={inputCls} />
                <textarea rows={Math.min(8, Math.max(3, (t.body || "").split("\n").length + 1))} value={t.body}
                  onChange={(e) => setTpls((x) => ({ ...x, [key]: { ...x[key], body: e.target.value } }))}
                  placeholder="Message (optional for invoices/quotes)"
                  data-testid={`tpl-body-${key}`}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                <button onClick={() => save(key)} disabled={savingTpl === key} data-testid={`tpl-save-${key}`}
                  className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-xs font-bold transition-colors">
                  {savingTpl === key ? "Saving…" : "Save wording"}
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default function AdminEmails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const editorBodyRef = useRef(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [showScheduled, setShowScheduled] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ host: "", port: 993, user: "", password: "", smtp_host: "", smtp_port: 587, signature: "" });
  const [showSettings, setShowSettings] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("INBOX");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [reading, setReading] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const [moveDest, setMoveDest] = useState("");
  const [compose, setCompose] = useState(null); // {to, cc, subject, text}
  const [sending, setSending] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [todoFor, setTodoFor] = useState(null);
  const [contactFor, setContactFor] = useState(null);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [searchParams] = useSearchParams();

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mailbox/folders");
      setFolders(data);
    } catch { /* folders optional */ }
  }, []);

  const loadList = useCallback(async (folder, q = "", force = false, retried = false) => {
    setLoadingList(true);
    try {
      const params = q ? { folder, q } : { folder };
      if (force) params.refresh = "1";
      const { data } = await api.get("/admin/mailbox/inbox", { params });
      setList(data);
      setSelected(new Set());
    } catch (err) {
      if (!retried) {
        setTimeout(() => loadList(folder, q, force, true), 1500);
        return;
      }
      toast.error(formatApiError(err));
      setList(null);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const runSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    setActiveQuery(q);
    loadList(activeFolder, q);
  };

  const clearSearch = () => {
    setQuery("");
    setActiveQuery("");
    loadList(activeFolder);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mailbox");
      setCfg(data);
      setForm((f) => ({ ...f, host: data.host, port: data.port, user: data.user, smtp_host: data.smtp_host, smtp_port: data.smtp_port, signature: data.signature || "" }));
      if (data.configured) {
        setShowSettings(false);
        loadFolders();
        loadList(activeFolder);
      } else {
        setShowSettings(true);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [activeFolder, loadFolders, loadList]);

  useEffect(() => { load(); }, [load]);

  const switchFolder = (name) => {
    setActiveFolder(name);
    setReading(null);
    setQuery("");
    setActiveQuery("");
    loadList(name);
  };

  const refreshAll = () => { loadFolders(); loadList(activeFolder, activeQuery, true); };

  const saveSettings = async (e) => {
    e.preventDefault();
    const gmail = /@(gmail|googlemail)\.com$/i.test(form.user);
    const preset = gmail ? PRESETS.gmail : PRESETS.microsoft;
    const payload = {
      ...form,
      host: form.host || preset.host,
      port: form.host ? Number(form.port) : preset.port,
      smtp_host: form.smtp_host || preset.smtp_host,
      smtp_port: form.smtp_host ? Number(form.smtp_port) : preset.smtp_port,
    };
    try {
      const { data } = await api.put("/admin/mailbox", payload);
      toast.success(data.message);
      setShowSettings(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openMessage = async (uid, folder) => {
    const f = folder || activeFolder;
    try {
      const { data } = await api.get(`/admin/mailbox/message/${uid}`, { params: { folder: f } });
      setReading(data);
      setList((l) => l && l.map((m) => (m.uid === uid ? { ...m, seen: true } : m)));
      loadFolders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (cfg && !cfg.configured) {
      if (searchParams.get("compose") === "1")
        toast.error("Connect your mailbox on this page first — then tap the email button again to start your message.");
      return;
    }
    if (!cfg?.configured) return;
    const uid = searchParams.get("uid");
    if (uid) {
      const folder = searchParams.get("folder") || "INBOX";
      setActiveFolder(folder);
      loadList(folder);
      openMessage(uid, folder);
    } else if (searchParams.get("compose") === "1") {
      setCompose({ to: searchParams.get("to") || "", cc: "", subject: searchParams.get("subject") || "", text: sigBlock(), attachments: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.configured]);

  useEffect(() => {
    if (!contactFor || clients.length) return;
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, [contactFor, clients.length]);

  const saveTodo = async () => {
    try {
      await api.post("/crm/tasks", {
        title: todoFor.title, kind: "general", due_date: todoFor.due_date, due_time: todoFor.due_time,
        contact_name: senderName(reading.from),
        email_uid: reading.uid, email_folder: activeFolder,
        email_from: reading.from, email_subject: reading.subject,
      });
      toast.success("Added to To-Do — open it there to jump back to this email");
      setTodoFor(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveContact = async () => {
    if (contactFor.client_id === "__new__" && !contactFor.company.trim()) { toast.error("Type the new company's name"); return; }
    if (!contactFor.client_id) { toast.error("Pick which company they belong to — or choose New company"); return; }
    try {
      let cid = contactFor.client_id;
      if (cid === "__new__") {
        const { data } = await api.post("/clients", {
          name: contactFor.company.trim(), email: contactFor.email.trim(), phone: "",
          address: "", notes: "Added from an email", bank_account_id: "", default_price: null,
        });
        cid = data.id;
      }
      await api.post(`/clients/${cid}/contacts`, {
        name: contactFor.name.trim(), email: contactFor.email.trim(), role: contactFor.role.trim(), phone: "",
      });
      toast.success(cid === contactFor.client_id ? "Contact added" : `Company "${contactFor.company.trim()}" created with this contact`);
      setContactFor(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSelect = (uid) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(uid) ? n.delete(uid) : n.add(uid);
      return n;
    });
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.post("/admin/mailbox/flags", { uids: [], folder: activeFolder, seen: true });
      toast.success("All marked as read");
      refreshAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteUids = async (uids) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mailbox/delete", { uids, folder: activeFolder });
      toast.success(data.message);
      setReading((r) => (r && uids.includes(r.uid) ? null : r));
      refreshAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const moveSelected = async () => {
    if (!moveDest || selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mailbox/move", { uids: [...selected], folder: activeFolder, dest: moveDest });
      toast.success(data.message);
      setMoveDest("");
      refreshAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try {
      const { data } = await api.post("/admin/mailbox/folders", { name: newFolder.trim() });
      toast.success(data.message);
      setNewFolder("");
      loadFolders();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const { data } = await api.get(`/admin/mailbox/message/${reading.uid}/attachment/${att.index}`, {
        params: { folder: activeFolder }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openReply = () => {
    const quoted = reading.body ? `\n\n---\nOn ${reading.date}, ${reading.from} wrote:\n${reading.body.split("\n").map((l) => `> ${l}`).join("\n")}` : "";
    setCompose({ to: senderEmail(reading.from), cc: "", subject: reading.subject.startsWith("Re:") ? reading.subject : `Re: ${reading.subject}`, text: sigBlock() + quoted, attachments: [] });
  };

  const openReplyAll = () => {
    const quoted = reading.body ? `\n\n---\nOn ${reading.date}, ${reading.from} wrote:\n${reading.body.split("\n").map((l) => `> ${l}`).join("\n")}` : "";
    const own = (cfg?.user || "").toLowerCase();
    const ccList = [...(reading.to || "").split(","), ...(reading.cc || "").split(",")]
      .map((x) => x.trim()).filter((x) => x && (!own || !x.toLowerCase().includes(own)));
    setCompose({ to: senderEmail(reading.from), cc: ccList.join(", "), subject: reading.subject.startsWith("Re:") ? reading.subject : `Re: ${reading.subject}`, text: sigBlock() + quoted, attachments: [] });
  };

  const openForward = () => {
    const fwd = `\n\n---------- Forwarded message ----------\nFrom: ${reading.from}\nDate: ${reading.date}\nSubject: ${reading.subject}\n\n${reading.body || "(see original for formatting)"}`;
    setCompose({ to: "", cc: "", subject: reading.subject.startsWith("Fwd:") ? reading.subject : `Fwd: ${reading.subject}`, text: sigBlock() + fwd, attachments: [] });
  };

  const sigBlock = () => (cfg?.signature ? `\n\n${cfg.signature}` : "");

  const addFiles = async (files) => {
    const list = [...files];
    const total = (compose?.attachments || []).reduce((a, x) => a + (x.size || 0), 0) + list.reduce((a, x) => a + x.size, 0);
    if (total > 8 * 1024 * 1024) { toast.error("Attachments too big — keep them under 8 MB altogether"); return; }
    const reads = await Promise.all(list.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ filename: f.name, content_type: f.type || "application/octet-stream", size: f.size, data: String(r.result).split(",")[1] });
      r.readAsDataURL(f);
    })));
    setCompose((c) => ({ ...c, attachments: [...(c.attachments || []), ...reads] }));
  };

  const insertVoiceText = (t) => {
    const el = editorBodyRef.current;
    if (el) {
      el.focus();
      document.execCommand("insertText", false, t + " ");
      setCompose((c) => ({ ...c, html: el.innerHTML, text: stripHtml(el.innerHTML) }));
    } else {
      setCompose((c) => ({ ...c, text: c.text ? `${c.text} ${t}` : t, html: (c.html || "") + escHtml(t) + " " }));
    }
  };

  const insertSignature = async () => {
    try {
      const { data } = await api.get("/admin/mailbox/signature/html");
      const el = editorBodyRef.current;
      if (el) {
        el.focus();
        el.innerHTML += `<br>${data.html}`;
        setCompose((c) => ({ ...c, html: el.innerHTML, text: stripHtml(el.innerHTML) }));
      } else {
        setCompose((c) => ({ ...c, text: (c.text || "") + "\n\n" + data.text }));
      }
      toast.success("Signature added");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const polish = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/polish", { text: compose.text, context: "professional business email for an event medical services company" });
      setCompose((c) => ({ ...c, text: data.polished, html: textToHtml(data.polished) }));
      toast.success("Polished");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPolishing(false);
    }
  };

  useEffect(() => {
    if (!compose || clients.length) return;
    api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, [compose, clients.length]);

  const sendCompose = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/mailbox/send", compose);
      toast.success(data.message);
      setCompose(null);
      setShowSchedule(false);
      setScheduleAt("");
      if (folderRank(activeFolder) === 1) loadList(activeFolder);
      loadFolders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const scheduleSend = async () => {
    if (!scheduleAt) { toast.error("Pick the date and time to send it"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/mailbox/schedule", { ...compose, send_at: new Date(scheduleAt).toISOString() });
      toast.success(`Scheduled for ${new Date(data.send_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`);
      setCompose(null);
      setShowSchedule(false);
      setScheduleAt("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const loadScheduled = async () => {
    try {
      const { data } = await api.get("/admin/mailbox/scheduled");
      setScheduledList(data || []);
    } catch { /* optional */ }
  };

  const cancelScheduled = async (id) => {
    try {
      await api.delete(`/admin/mailbox/scheduled/${id}`);
      toast.success("Scheduled email cancelled");
      loadScheduled();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sortedFolders = useMemo(() => [...folders].sort((a, b) => folderRank(a.name) - folderRank(b.name) || a.name.localeCompare(b.name)), [folders]);
  const totalUnseen = folders.filter((f) => f.name.toLowerCase() === "inbox").reduce((a, f) => a + f.unseen, 0);
  const htmlDoc = useMemo(() => reading?.body_html ? `<base target="_blank">${reading.body_html}` : "", [reading]);

  if (user && !user.is_owner) {
    return (
      <div className="min-h-screen bg-[#F4F7FA] grid place-items-center p-6" data-testid="emails-owner-only">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md text-center">
          <Mail className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <h1 className="font-heading text-xl font-bold text-[#0F2547]">Owner only</h1>
          <p className="text-sm text-slate-500 mt-2">The mailbox is only available on the account owner's login. Other admin accounts can't read or send emails here.</p>
          <button onClick={() => navigate("/admin")} data-testid="emails-owner-back"
            className="mt-4 h-11 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold transition-colors">
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="admin-emails">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="emails-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547] flex items-center gap-2">
              Emails
              {totalUnseen > 0 && <span className="text-xs font-bold bg-[#005EB8] text-white rounded-full px-2 py-0.5" data-testid="inbox-unread-badge">{totalUnseen} new</span>}
            </h1>
            <p className="text-xs text-slate-500 truncate">{cfg?.configured ? cfg.user : "Connect your mailbox to read & reply here"}</p>
          </div>
          <button onClick={() => navigate("/admin/emails/signature")} data-testid="signature-builder-btn" title="Build your email signature"
            className="h-10 w-10 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
            <PenLine className="h-4 w-4" />
          </button>
          {cfg?.configured && (
            <>

              <button onClick={() => setShowSettings((s) => !s)} data-testid="mailbox-settings-btn"
                className="h-10 w-10 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <Settings className="h-4 w-4" />
              </button>
              <button onClick={() => { setShowScheduled(true); loadScheduled(); }} data-testid="scheduled-btn" title="Scheduled emails"
                className="h-10 w-10 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                <Clock className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4">
        {(showSettings || (cfg && !cfg.configured)) && (
          <form onSubmit={saveSettings} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="mailbox-form">
            <h2 className="font-heading text-base font-bold text-[#0F2547]">Connect your mailbox — once only</h2>
            <p className="text-xs text-slate-500">Enter your email address and password once. After that your inbox loads automatically every time you open this page — nothing else to do.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <input type="email" required value={form.user} onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                placeholder="Email address (e.g. info@waleseventmedical.co.uk)" data-testid="mailbox-user" className={inputCls} />
              <input type="password" required value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Password / app password" data-testid="mailbox-password" className={inputCls} />
            </div>
            <button type="button" onClick={() => setShowAdvanced((a) => !a)} data-testid="mailbox-advanced-toggle"
              className="text-xs font-bold text-[#005EB8] hover:underline">
              {showAdvanced ? "Hide server settings" : "Advanced server settings (only if auto-detect fails)"}
            </button>
            {showAdvanced && (
              <div className="space-y-2">
                <div className="flex gap-2 flex-wrap">
                  {Object.values(PRESETS).map((p) => (
                    <button type="button" key={p.label} onClick={() => setForm((f) => ({ ...f, ...p }))} data-testid={`preset-${p.host}`}
                      className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] transition-colors">
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                    placeholder="IMAP host (incoming) — auto-detected if blank" data-testid="mailbox-host" className={inputCls} />
                  <input value={form.smtp_host} onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))}
                    placeholder="SMTP host (outgoing) — auto-detected if blank" data-testid="mailbox-smtp" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input type="number" value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: Number(e.target.value) || 993 }))}
                    placeholder="Incoming (IMAP) port — usually 993" data-testid="mailbox-port" className={inputCls} />
                  <input type="number" value={form.smtp_port} onChange={(e) => setForm((f) => ({ ...f, smtp_port: Number(e.target.value) || 587 }))}
                    placeholder="Outgoing (SMTP) port — usually 587" data-testid="mailbox-smtp-port" className={inputCls} />
                </div>
              </div>
            )}
            <textarea rows={3} value={form.signature} onChange={(e) => setForm((f) => ({ ...f, signature: e.target.value }))}
              placeholder="Email signature (optional) — added to the bottom of every email you send" data-testid="mailbox-signature"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <p className="text-[11px] text-slate-400">Microsoft 365 / Gmail may need an "app password" instead of your normal password — your email provider's security settings page generates one. Server settings are auto-detected from your email address. This mailbox is yours alone — each admin connects their own email here.</p>
            <button type="submit" data-testid="mailbox-save"
              className="h-11 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold transition-colors">
              Save &amp; connect
            </button>
          </form>
        )}

        {(showSettings || (cfg && !cfg.configured)) && <EmailTemplateEditor />}

        {cfg?.configured && !showSettings && (
          <div className="flex flex-col lg:flex-row lg:items-start gap-3">
            <aside className="w-full lg:w-44 shrink-0 flex lg:block gap-2 lg:gap-0 lg:space-y-0.5 overflow-x-auto items-start pb-1 lg:pb-0" data-testid="folder-sidebar">
              <button onClick={() => setCompose({ to: "", cc: "", subject: "", text: sigBlock(), attachments: [] })} data-testid="compose-btn"
                className="shrink-0 lg:w-full h-10 px-4 lg:px-0 lg:mb-2 rounded-xl bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition-colors shadow-sm">
                <PenSquare className="h-4 w-4" /> Compose
              </button>
              {sortedFolders.map((f) => {
                const meta = folderMeta(f.name);
                const active = activeFolder === f.name;
                return (
                  <button key={f.name} onClick={() => switchFolder(f.name)} data-testid={`folder-${meta.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`shrink-0 lg:w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors ${active ? "bg-sky-100 text-[#005EB8] font-bold" : "text-[#0F2547] hover:bg-white font-semibold"}`}>
                    <meta.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1 text-left">{meta.label}</span>
                    {f.unseen > 0 && <span className="text-[10px] font-bold bg-[#005EB8] text-white rounded-full px-1.5 py-0.5" data-testid={`folder-unseen-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{f.unseen}</span>}
                  </button>
                );
              })}
              <div className="shrink-0 min-w-[190px] lg:min-w-0 lg:pt-3 lg:mt-3 lg:border-t border-slate-200">
                <div className="flex gap-1.5">
                  <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} placeholder="New box name"
                    data-testid="new-folder-input"
                    className="flex-1 min-w-0 h-9 rounded-lg border border-slate-300 px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <button onClick={createFolder} data-testid="new-folder-btn" aria-label="Create box"
                    className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] transition-colors">
                    <FolderPlus className="h-4 w-4" />
                  </button>
                </div>
                <p className="hidden lg:block text-[10px] text-slate-400 mt-1.5">Boxes are real folders in your mailbox — they show up in Outlook on your phone too.</p>
              </div>
            </aside>

            <section className={`w-full lg:w-72 shrink-0 bg-white rounded-2xl border border-slate-200 ${reading ? "hidden lg:flex" : "flex"} flex-col`} data-testid="inbox-list">
              <form onSubmit={runSearch} className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100" data-testid="email-search-form">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search this box — name, subject or address" data-testid="email-search-input"
                  className="flex-1 min-w-0 h-9 rounded-lg border border-slate-300 px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                <button type="submit" disabled={loadingList} data-testid="email-search-btn" aria-label="Search emails"
                  className="h-9 w-9 grid place-items-center rounded-lg bg-[#005EB8] text-white hover:bg-[#004a93] disabled:opacity-50 transition-colors shrink-0">
                  <Search className="h-4 w-4" />
                </button>
                {activeQuery && (
                  <button type="button" onClick={clearSearch} data-testid="email-search-clear" aria-label="Clear search"
                    className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:border-[#D5281B] hover:text-[#D5281B] transition-colors shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </form>
              {activeQuery && (
                <p className="px-3 py-1.5 text-[10px] font-bold text-[#005EB8] bg-sky-50 border-b border-slate-100" data-testid="email-search-note">
                  Showing matches for "{activeQuery}" in {folderMeta(activeFolder).label}
                </p>
              )}
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
                <input type="checkbox" data-testid="select-all-checkbox"
                  checked={Boolean(list?.length) && selected.size === list.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(list.map((m) => m.uid)) : new Set())}
                  className="h-4 w-4 accent-[#005EB8]" />
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide flex-1">
                  {list ? `${list.length} email${list.length === 1 ? "" : "s"}` : ""}
                </span>
                <button onClick={markAllRead} disabled={busy} data-testid="mark-all-read-btn"
                  className="h-8 px-2 rounded-md text-[11px] font-bold text-[#005EB8] hover:bg-sky-50 transition-colors inline-flex items-center gap-1">
                  <MailOpen className="h-3.5 w-3.5" /> All read
                </button>
                <button onClick={() => deleteUids([...selected])} disabled={busy || selected.size === 0} data-testid="delete-selected-btn"
                  className="h-8 px-2 rounded-md text-[11px] font-bold text-[#D5281B] hover:bg-red-50 disabled:opacity-40 transition-colors inline-flex items-center gap-1">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
              {selected.size > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-sky-50/50">
                  <select value={moveDest} onChange={(e) => setMoveDest(e.target.value)} data-testid="move-dest-select"
                    className="flex-1 h-8 rounded-md border border-slate-300 px-2 text-xs bg-white">
                    <option value="">Move {selected.size} selected to…</option>
                    {sortedFolders.filter((f) => f.name !== activeFolder).map((f) => (
                      <option key={f.name} value={f.name}>{folderMeta(f.name).label}</option>
                    ))}
                  </select>
                  <button onClick={moveSelected} disabled={busy || !moveDest} data-testid="move-selected-btn"
                    className="h-8 px-3 rounded-md bg-[#005EB8] text-white text-[11px] font-bold disabled:opacity-40 transition-colors">Move</button>
                </div>
              )}
              <div className="divide-y divide-slate-100">
                {list === null ? (
                  <p className="text-xs text-slate-400 p-4">{loadingList ? "Checking your mail…" : "Couldn't load — check settings."}</p>
                ) : list.length === 0 ? (
                  <p className="text-xs text-slate-400 p-4">This box is empty.</p>
                ) : list.map((m) => (
                  <div key={m.uid} className={`flex items-start gap-2 px-2.5 py-2 hover:bg-sky-50 transition-colors cursor-pointer ${reading?.uid === m.uid ? "bg-sky-50" : ""}`}
                    data-testid={`inbox-row-${m.uid}`}>
                    <input type="checkbox" checked={selected.has(m.uid)} onChange={() => toggleSelect(m.uid)}
                      onClick={(e) => e.stopPropagation()} data-testid={`select-${m.uid}`} className="h-4 w-4 mt-1 accent-[#005EB8] shrink-0" />
                    <button onClick={() => openMessage(m.uid)} className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-1.5">
                        {!m.seen && <span className="h-2 w-2 rounded-full bg-[#005EB8] shrink-0" data-testid={`unread-dot-${m.uid}`} />}
                        <p className={`text-sm truncate flex-1 ${m.seen ? "font-medium text-slate-600" : "font-bold text-[#0F2547]"}`}>{senderName(m.from)}</p>
                        <span className="text-[10px] text-slate-400 shrink-0">{shortDate(m.date)}</span>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${m.seen ? "text-slate-400" : "font-semibold text-slate-700"}`}>{m.subject}</p>
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className={`${reading ? "block" : "hidden"} lg:block flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 p-4 sm:p-5`} data-testid="message-pane">
              {reading && (
                <button onClick={() => setReading(null)} data-testid="message-back"
                  className="lg:hidden mb-3 h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] inline-flex items-center gap-1.5">
                  <ChevronLeft className="h-4 w-4" /> Back to inbox
                </button>
              )}
              {!reading ? (
                <div className="h-full grid place-items-center text-center py-16">
                  <div>
                    <Mail className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">Pick an email to read it, or hit Compose to write one.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 flex-wrap border-b border-slate-100 pb-3 mb-4">
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-[#0F2547] text-lg leading-tight" data-testid="message-subject">{reading.subject}</p>
                      <p className="text-xs text-slate-500 mt-1">From: {reading.from}</p>
                      {reading.to && <p className="text-[11px] text-slate-400">To: {reading.to} · {reading.date}</p>}
                      {reading.cc && <p className="text-[11px] text-slate-400">Cc: {reading.cc}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={openReply} data-testid="reply-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                        <Reply className="h-3.5 w-3.5" /> Reply
                      </button>
                      <button onClick={openReplyAll} data-testid="reply-all-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                        <Reply className="h-3.5 w-3.5" /> Reply all
                      </button>
                      <button onClick={openForward} data-testid="forward-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                        <Forward className="h-3.5 w-3.5" /> Forward
                      </button>
                      <button onClick={() => setTodoFor({ title: `Email — ${reading.subject}`, due_date: "", due_time: "" })} data-testid="email-todo-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                        <ListTodo className="h-3.5 w-3.5" /> To-do
                      </button>
                      <button onClick={() => setContactFor({ client_id: "", company: "", name: senderName(reading.from), email: senderEmail(reading.from), role: "" })} data-testid="email-add-contact-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                        <UserPlus className="h-3.5 w-3.5" /> Add to contacts
                      </button>
                      <button onClick={() => deleteUids([reading.uid])} disabled={busy} data-testid="delete-btn"
                        className="h-10 px-3.5 rounded-lg border border-slate-300 text-xs font-bold text-[#D5281B] hover:border-[#D5281B] hover:bg-red-50 transition-colors inline-flex items-center gap-1.5">
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                  {reading.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="attachment-list">
                      {reading.attachments.map((att) => (
                        <button key={att.index} onClick={() => downloadAttachment(att)} data-testid={`attachment-${att.index}`}
                          className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-semibold text-[#005EB8] hover:border-[#005EB8] transition-colors inline-flex items-center gap-1.5">
                          <Paperclip className="h-3.5 w-3.5" /> {att.filename}
                          <span className="text-slate-400">({Math.max(1, Math.round(att.size / 1024))} KB)</span>
                          <Download className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  )}
                  {reading.body_html ? (
                    <iframe title="email-body" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={htmlDoc}
                      className="w-full h-[65vh] border-0" data-testid="message-body-html" />
                  ) : (
                    <div className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="message-body">
                      {reading.body || "(no plain-text body)"}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {cfg && !cfg.configured && !showSettings && (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="emails-empty">
            <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No mailbox connected yet — add your email settings above to read &amp; reply here.</p>
          </div>
        )}
      </main>

      <Dialog open={Boolean(compose)} onOpenChange={(v) => !v && setCompose(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">New message</DialogTitle>
          </DialogHeader>
          {compose && (
            <div className="space-y-2.5">
              <div className="flex gap-2">
                <input value={compose.to} onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                  placeholder="To" data-testid="compose-to" className={`${inputCls} flex-1`} />
                <ContactPicker clients={clients} testid="compose-to-picker"
                  onPick={(em) => setCompose((c) => ({ ...c, to: c.to ? `${c.to}, ${em}` : em }))} />
              </div>
              <div className="flex gap-2">
                <input value={compose.cc} onChange={(e) => setCompose((c) => ({ ...c, cc: e.target.value }))}
                  placeholder="Cc (optional)" data-testid="compose-cc" className={`${inputCls} flex-1`} />
                <ContactPicker clients={clients} testid="compose-cc-picker"
                  onPick={(em) => setCompose((c) => ({ ...c, cc: c.cc ? `${c.cc}, ${em}` : em }))} />
              </div>
              <div className="flex gap-2 items-center">
                <input value={compose.subject} onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                  placeholder="Subject" data-testid="compose-subject" className={`${inputCls} flex-1`} />
                <VoiceButton testid="compose-subject-mic"
                  onTranscript={(t) => setCompose((c) => ({ ...c, subject: c.subject ? `${c.subject} ${t}` : t }))} />
              </div>
              <div className="relative">
                <RichEditor value={compose.html ?? textToHtml(compose.text)} editorRef={editorBodyRef} testid="compose-editor"
                  onChange={(h) => setCompose((c) => ({ ...c, html: h, text: stripHtml(h) }))} />
                <div className="flex justify-end mt-1">
                  <VoiceButton testid="compose-body-mic" onTranscript={insertVoiceText} />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label data-testid="compose-attach-label"
                  className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors inline-flex items-center gap-1.5 cursor-pointer">
                  <Paperclip className="h-3.5 w-3.5" /> Attach files
                  <input type="file" multiple className="hidden" data-testid="compose-attachment-input"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                </label>
                <button onClick={polish} disabled={polishing || !compose.text.trim()} data-testid="compose-polish-btn"
                  className="h-9 px-3 rounded-lg border border-slate-300 text-xs font-bold text-[#005EB8] hover:border-[#005EB8] disabled:opacity-40 transition-colors inline-flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> {polishing ? "Polishing…" : "Polish with AI"}
                </button>
              </div>
              {compose.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="compose-attachment-list">
                  {compose.attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-slate-100 rounded-full px-2.5 py-1" data-testid={`compose-attachment-${i}`}>
                      <Paperclip className="h-3 w-3" /> {a.filename}
                      <button onClick={() => setCompose((c) => ({ ...c, attachments: c.attachments.filter((_, j) => j !== i) }))}
                        data-testid={`compose-attachment-remove-${i}`} aria-label={`Remove ${a.filename}`}
                        className="text-slate-400 hover:text-[#D5281B] font-bold">×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={insertSignature} data-testid="compose-signature-btn" title="Add your signature to this email"
                  className="h-11 px-4 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-sm font-bold inline-flex items-center gap-2 transition-colors">
                  <PenLine className="h-4 w-4" /> Signature
                </button>
                <button onClick={sendCompose} disabled={sending || !compose.to.trim() || !compose.text.trim()} data-testid="compose-send"
                  className="h-11 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2 transition-colors">
                  <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send"}
                </button>
                <button onClick={() => setShowSchedule((s) => !s)} disabled={sending || !compose.to.trim() || !compose.text.trim()} data-testid="compose-schedule-btn"
                  className="h-11 px-5 rounded-lg border border-slate-300 disabled:opacity-50 text-[#0F2547] text-sm font-bold inline-flex items-center gap-2 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors">
                  <CalendarClock className="h-4 w-4" /> Schedule
                </button>
              </div>
              {showSchedule && (
                <div className="flex items-center gap-2 flex-wrap rounded-lg border border-sky-200 bg-sky-50 p-2.5" data-testid="compose-schedule-row">
                  <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                    data-testid="compose-schedule-at"
                    className="h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                  <button onClick={scheduleSend} disabled={sending || !scheduleAt} data-testid="compose-schedule-confirm"
                    className="h-10 px-4 rounded-lg bg-[#0F2547] hover:bg-[#173766] disabled:opacity-50 text-white text-xs font-bold transition-colors">
                    Schedule for this time
                  </button>
                  <p className="text-[10px] text-slate-500 w-full">Sends automatically within about 15 minutes of the time you pick. See pending ones via the clock icon at the top.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showScheduled} onOpenChange={setShowScheduled}>
        <DialogContent className="sm:max-w-md" data-testid="scheduled-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Scheduled emails</DialogTitle>
          </DialogHeader>
          {scheduledList.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="scheduled-empty">Nothing scheduled right now.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {scheduledList.map((m) => (
                <div key={m.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`scheduled-item-${m.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#0F2547] truncate">{m.subject || "(no subject)"}</p>
                    <p className="text-[11px] text-slate-500 truncate">To: {m.to}</p>
                    <p className="text-[11px] text-[#005EB8] font-semibold">
                      Sends {new Date(m.send_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <button onClick={() => cancelScheduled(m.id)} data-testid={`scheduled-cancel-${m.id}`}
                    className="h-9 px-3 rounded-lg border border-slate-300 text-[11px] font-bold text-[#D5281B] hover:border-[#D5281B] hover:bg-red-50 transition-colors shrink-0">
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(todoFor)} onOpenChange={(v) => !v && setTodoFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="email-todo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Make this a To-Do</DialogTitle>
          </DialogHeader>
          {todoFor && (
            <div className="space-y-2.5">
              <input value={todoFor.title} onChange={(e) => setTodoFor((t) => ({ ...t, title: e.target.value }))}
                placeholder="What needs doing?" data-testid="email-todo-title" className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={todoFor.due_date} onChange={(e) => setTodoFor((t) => ({ ...t, due_date: e.target.value }))}
                  data-testid="email-todo-date" className={inputCls} />
                <input type="time" value={todoFor.due_time} onChange={(e) => setTodoFor((t) => ({ ...t, due_time: e.target.value }))}
                  data-testid="email-todo-time" className={inputCls} />
              </div>
              <p className="text-[11px] text-slate-400">The job links back to this email, so you can open and action it from To-Do.</p>
              <button onClick={saveTodo} disabled={!todoFor.title.trim()} data-testid="email-todo-save"
                className="h-11 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold transition-colors">
                Add to To-Do
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(contactFor)} onOpenChange={(v) => !v && setContactFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="email-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Add sender to contacts</DialogTitle>
          </DialogHeader>
          {contactFor && (
            <div className="space-y-2.5">
              <select value={contactFor.client_id} onChange={(e) => setContactFor((c) => ({ ...c, client_id: e.target.value }))}
                data-testid="email-contact-client" className={`${inputCls} bg-white`}>
                <option value="">Which company do they belong to?</option>
                <option value="__new__">＋ New company…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {contactFor.client_id === "__new__" && (
                <input value={contactFor.company} onChange={(e) => setContactFor((c) => ({ ...c, company: e.target.value }))}
                  placeholder="New company name" data-testid="email-contact-company" className={inputCls} />
              )}
              <input value={contactFor.name} onChange={(e) => setContactFor((c) => ({ ...c, name: e.target.value }))}
                placeholder="Their name" data-testid="email-contact-name" className={inputCls} />
              <input type="email" value={contactFor.email} onChange={(e) => setContactFor((c) => ({ ...c, email: e.target.value }))}
                placeholder="Their email" data-testid="email-contact-email" className={inputCls} />
              <input value={contactFor.role} onChange={(e) => setContactFor((c) => ({ ...c, role: e.target.value }))}
                placeholder="Role (optional, e.g. Event organiser)" data-testid="email-contact-role" className={inputCls} />
              <button onClick={saveContact} disabled={!contactFor.client_id || !contactFor.name.trim() || (contactFor.client_id === "__new__" && !contactFor.company.trim())} data-testid="email-contact-save"
                className="h-11 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold transition-colors">
                Save contact
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
